import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import paymentportalservice from "../../../services/paymentportalservice";
import Swal from "sweetalert2";
import Percentage from "./Percentage";
import portalpaymentlink from "../../../services/portalpaymentlink";
import { Link } from "react-router-dom";

const getVendorLabel = (vendor) => {
  if (!vendor) return "";
  if (typeof vendor === "string") return vendor;
  return vendor.name || vendor.label || vendor.vendorName || "";
};

const normalizeVendorPaymentDetails = (response) => {
  const payload = response?.data || response || {};

  return {
    maxPercentage: Number(payload['percentage']),
    totalAmount: Number(payload['totalCost']),
    description: payload['description'],
    isGstApplicable: payload['isGstPaid']
  };
};

const computeGstAmount = (totalAmount, isGstApplicable) => {
  if (!isGstApplicable) return "";

  const amount = Number(totalAmount || 0);
  if (!amount) return "";

  return String(Math.round(amount * 0.18));
};

const computePayableAmount = (totalAmount, percentage) => {
  const amount = Number(totalAmount || 0);
  const selectedPercentage = Number(percentage || 0);

  if (!amount || !selectedPercentage) return "";

  return String(Math.round((amount * selectedPercentage) / 100));
};

const toBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return Boolean(value);
};

export default function VendorForm({ campaignDetails = {}, vendors = [], styles }) {
  const [maxPercentage, setMaxPercentage] = useState(100);
  const [isTotalAmountDisabled, setIsTotalAmountDisabled] = useState(false);
  const [vendorList, setVendorList] = useState(vendors);
  const [isVendorDetailsLoading, setIsVendorDetailsLoading] = useState(false);
  const [campaignLimit, setCampaignLimit] = useState(Number(campaignDetails?.paymentLimit) || 0);
  const [formDisabled, setFormDisabled] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      vendor: null,
      campaign: campaignDetails?.name || "",
      description: "",
      expenseType: "",
      isGstApplicable: false,
      totalAmount: "",
      gstAmount: "",
      payableAmount: "",
      percentage: "",
    },
  });

  const watchedTotalAmount = useWatch({ control, name: "totalAmount" });
  const watchedGstApplicable = useWatch({ control, name: "isGstApplicable" });
  const watchedPercentage = useWatch({ control, name: "percentage" });

  const calculatedGstAmount = useMemo(
    () => computeGstAmount(watchedTotalAmount, watchedGstApplicable),
    [watchedTotalAmount, watchedGstApplicable]
  );

  useEffect(() => {
    setValue("campaign", campaignDetails?.name || "");
  }, [campaignDetails?.name, setValue]);

  useEffect(() => {
    let isMounted = true;

    const fetchLatestVendors = async () => {
      try {
        const latestVendors = await paymentportalservice.FetchVendors();

        if (isMounted && Array.isArray(latestVendors)) {
          setVendorList(latestVendors);
        }
      } catch (error) {
        console.log("Error in fetching latest vendors", error);
      }
    };

    fetchLatestVendors();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setValue("gstAmount", calculatedGstAmount);
  }, [calculatedGstAmount, setValue]);

  useEffect(() => {
    setValue(
      "payableAmount",
      computePayableAmount(watchedTotalAmount, watchedPercentage)
    );
  }, [watchedTotalAmount, watchedPercentage, setValue]);

  useEffect(() => {
    fetchCampaignDetails();
  }, [campaignDetails]);

  const fetchCampaignDetails = async () => {
    try {
      if (!campaignDetails.id) {
        return;
      }

      const details = await portalpaymentlink.getCampaignDetails({ id: campaignDetails.id });
      setCampaignLimit(details?.paymentLimit);
    }
    catch (error) {

    }
  }

  const handleVendorSelect = async (selectedVendor) => {
    setFormDisabled(false);
    if (!selectedVendor) {
      setMaxPercentage(100);
      setIsTotalAmountDisabled(false);
      setValue("description", "");
      setValue("expenseType", "");
      setValue("totalAmount", "");
      setValue("gstAmount", "");
      setValue("payableAmount", "");
      setValue("percentage", "");
      setValue("isGstApplicable", false);
      return;
    }

    try {
      setIsVendorDetailsLoading(true);
      console.log(`selectedVendor: `, selectedVendor);
      let postdata = {
        "company": `${selectedVendor._id}@@${getVendorLabel(selectedVendor)}`,
        "campaign": campaignDetails?.id
      }
      const response = await paymentportalservice.CalculateVendorPercentage(postdata);
      const vendorPaymentDetails = normalizeVendorPaymentDetails(response);
      const receivedMaxPercentage = Number(vendorPaymentDetails.maxPercentage);
      const nextMaxPercentage = Math.max(
        0,
        Math.floor(Math.min(Number.isFinite(receivedMaxPercentage) ? receivedMaxPercentage : 100, 100))
      );

      setMaxPercentage(nextMaxPercentage);
      setValue("percentage", "");
      setValue("payableAmount", "");

      if (Number.isFinite(vendorPaymentDetails.totalAmount)) {
        setValue("totalAmount", String(vendorPaymentDetails.totalAmount || ""));
        setIsTotalAmountDisabled(vendorPaymentDetails.totalAmount > 0);
      } else {
        setIsTotalAmountDisabled(false);
      }

      if (vendorPaymentDetails.description !== undefined) {
        setValue("description", vendorPaymentDetails.description);
      }

      if (vendorPaymentDetails.expenseType !== undefined) {
        setValue("expenseType", vendorPaymentDetails.expenseType);
      }

      if (vendorPaymentDetails.isGstApplicable !== undefined) {
        setValue("isGstApplicable", toBoolean(vendorPaymentDetails.isGstApplicable));
      }
    }
    catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Error in fetching vendor details';
      Swal.fire({
        title: 'Payment Link Error',
        text: errorMessage,
        icon: 'error'
      });
      setFormDisabled(true);
      setIsTotalAmountDisabled(true);
    }
    finally {
      setIsVendorDetailsLoading(false);
    }
  }

  const validateBeforeSubmit = (data) => {
    clearErrors();

    const validationErrors = [];
    const totalAmount = Number(data?.totalAmount);
    const percentage = Number(data?.percentage);
    const payableAmount = Number(data?.payableAmount);

    if (!data?.vendor) {
      validationErrors.push("Please select a vendor.");
      setError("vendor", { type: "manual", message: "Vendor is required" });
    }

    if (!data?.description?.trim()) {
      validationErrors.push("Please enter a description.");
      setError("description", { type: "manual", message: "Description is required" });
    }

    if (!Number.isFinite(totalAmount) || totalAmount <= 0) {
      validationErrors.push("Please enter a valid total amount.");
      setError("totalAmount", {
        type: "manual",
        message: "Total amount must be greater than 0",
      });
    }

    if (!Number.isFinite(percentage) || percentage <= 0) {
      validationErrors.push("Please select a percentage.");
      setError("percentage", {
        type: "manual",
        message: "Please select a percentage",
      });
    } else if (percentage > maxPercentage) {
      validationErrors.push(`Percentage cannot be more than ${maxPercentage}%.`);
      setError("percentage", {
        type: "manual",
        message: `Percentage cannot be more than ${maxPercentage}%`,
      });
    }

    if (!Number.isFinite(payableAmount) || payableAmount <= 0) {
      validationErrors.push("Payable amount should be greater than 0.");
      setError("payableAmount", {
        type: "manual",
        message: "Payable amount should be greater than 0",
      });
    }

    if (!campaignDetails?.id) {
      validationErrors.push("Campaign details are missing.");
    }

    if (validationErrors.length > 0) {
      Swal.fire({
        title: "Please check the form",
        html: validationErrors.join("<br />"),
        icon: "warning",
      });
      return false;
    }

    return true;
  };

  const onSubmit = async (data, event) => {
    const action = event?.nativeEvent?.submitter?.value;

    if (!validateBeforeSubmit(data)) return;

    console.log("Vendor payment form data:", data);
    let postData = {
      company: data?.vendor?._id || data?.vendor?.id,
      companyName: getVendorLabel(data?.vendor),
      percentage: data?.percentage,
      cost: Number(data?.totalAmount),
      description: data?.description,
      isGstPaid: false,
      expensesCategory: data?.expenseType,
      gstAmount: data?.gstAmount,
      payableAmount: data?.payableAmount,
      paymentLimit: campaignDetails?.paymentLimit,
      campaign: campaignDetails?.id,
      isGst: data?.isGstApplicable,
      isCompany: true,
      isGstPay: false
    };

    try {
      const response = await paymentportalservice.CreateVendorPaymentLink(postData);

      if (action === "add_another") {
        window.location.reload();
      }
      else {
        window.location.href = "/payments/status";
      }
    }
    catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || "Error in creating vendor payment link";
      Swal.fire({
        title: "Payment Link Error",
        text: errorMessage,
        icon: "error",
      });
    }
  };

  const onInvalidSubmit = (formErrors) => {
    const validationErrors = Object.values(formErrors)
      .map((error) => error?.message)
      .filter(Boolean);

    if (!getValues("percentage")) {
      validationErrors.push("Please select a percentage.");
      setError("percentage", {
        type: "manual",
        message: "Please select a percentage",
      });
    }

    Swal.fire({
      title: "Please check the form",
      html: validationErrors.join("<br />") || "Please fill all required fields.",
      icon: "warning",
    });
  };

  return (
    <Card sx={styles}>
      <CardContent>
        <Box component="form" onSubmit={handleSubmit(onSubmit, onInvalidSubmit)} noValidate>
          <Stack spacing={3}>
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
            >
              <Box>
                <Typography variant="h6">Vendor Payment Details</Typography>
                <Typography variant="body2" color="text.secondary">
                  Add vendor, expense, and payment details for this campaign.
                </Typography>
              </Box>

              <Link
                to={"/payments/status"}
                target="blank"
                style={{
                  fontFamily: "Quicksand"
                }}
              >
                Show Payment Links
              </Link>
            </Stack>

            <Divider />

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems="flex-start"
            >
              <Controller
                name="vendor"
                control={control}
                rules={{ required: "Vendor is required" }}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    disablePortal
                    id="vendor-dropdown"
                    options={vendorList}
                    getOptionLabel={getVendorLabel}
                    isOptionEqualToValue={(option, value) =>
                      getVendorLabel(option) === getVendorLabel(value)
                    }
                    onChange={(_, value) => {
                      field.onChange(value);
                      handleVendorSelect(value);
                    }}
                    sx={{ width: { xs: "100%", md: "50%" } }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Vendor*"
                        error={Boolean(errors.vendor)}
                        helperText={
                          isVendorDetailsLoading
                            ? "Loading vendor details..."
                            : errors.vendor?.message
                        }
                      />
                    )}
                  />
                )}
              />

              <Controller
                name="campaign"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="medium"
                    label="Campaign*"
                    variant="outlined"
                    sx={{ width: { xs: "100%", md: "50%" } }}
                    disabled
                  />
                )}
              />
            </Stack>

            <Controller
              name="description"
              control={control}
              rules={{ required: "Description is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description*"
                  multiline
                  rows={4}
                  fullWidth
                  error={Boolean(errors.description)}
                  helperText={errors.description?.message}
                />
              )}
            />

            <Controller
              name="expenseType"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="medium"
                  label="Type of Expenses (If Applicable)"
                  variant="outlined"
                  fullWidth
                />
              )}
            />

            <Controller
              name="isGstApplicable"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    calculatedGstAmount
                      ? `GST (If Applicable) - Rs. ${calculatedGstAmount}`
                      : "GST (If Applicable)"
                  }
                  sx={{ alignSelf: "flex-start" }}
                />
              )}
            />

            <Typography
              fontSize={15}
              fontFamily={'Quicksand'}
              fontWeight={600}
            >
              Campaign Budget Left:
              &nbsp;
              <span
                style={{
                  color: "#198754"
                }}
              >
                ₹{campaignLimit}
              </span>
            </Typography>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems="flex-start"
              sx={{
                "& > *": {
                  flex: 1,
                  width: { xs: "100%", md: "25%" },
                  minWidth: 0,
                },
              }}
            >
              <Controller
                name="totalAmount"
                control={control}
                rules={{ required: "Total amount is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    size="medium"
                    label="Total Amount*"
                    variant="outlined"
                    sx={{ width: "100%" }}
                    error={Boolean(errors.totalAmount)}
                    helperText={errors.totalAmount?.message}
                    disabled={isTotalAmountDisabled}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">Rs.</InputAdornment>,
                    }}
                  />
                )}
              />

              <Controller
                name="gstAmount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    size="medium"
                    label="GST Amount"
                    variant="outlined"
                    sx={{ width: "100%" }}
                    disabled
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">Rs.</InputAdornment>,
                    }}
                  />
                )}
              />

              <Controller
                name="payableAmount"
                control={control}
                rules={{ required: "Payable amount is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="number"
                    size="medium"
                    label="Payable Amount*"
                    variant="outlined"
                    sx={{ width: "100%" }}
                    error={Boolean(errors.payableAmount)}
                    helperText={errors.payableAmount?.message}
                    disabled
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">Rs.</InputAdornment>,
                    }}
                  />
                )}
              />

              <Box
                sx={{
                  width: "100%",
                }}
              >
                <Percentage
                  setValue={setValue}
                  getValues={getValues}
                  control={control}
                  width="100%"
                  maxPercentage={maxPercentage}
                  error={Boolean(errors.percentage)}
                />
                {errors.percentage?.message && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: "block", mt: 0.5, mx: 1.75 }}
                  >
                    {errors.percentage.message}
                  </Typography>
                )}
              </Box>
              {/* <Controller
                name="percentage"
                control={control}
                render={({ field }) => (
                  <FormControl sx={{ width: { xs: "100%", md: "10%" }, minWidth: 96 }}>
                    <InputLabel id="percentage-label">%</InputLabel>
                    <Select
                      {...field}
                      labelId="percentage-label"
                      id="percentage"
                      label="%"
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value={1}>1%</MenuItem>
                    </Select>
                  </FormControl>
                )}
              /> */}
            </Stack>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: 'center',
                columnGap: 2
              }}
            >
              <Button
                type="submit"
                variant="contained"
                value="submit"
                disabled={formDisabled}
              >
                Submit
              </Button>

              <Button
                type="submit"
                value="add_another"
                variant="outlined"
                disabled={formDisabled}
              >
                Add Another
              </Button>
            </Box>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}

/*
Original code kept for reference:

export default function VendorForm({ campaignDetails, vendors, styles }) {
  return (
    <>
      <Card
        sx={styles}
      >
        <CardContent>
          <Stack spacing={2}>
            <Stack direction={"row"} spacing={2}>
              <Autocomplete
                disablePortal
                id="brand-dropdown"
                options={vendors}
                sx={{ width: "50%" }}
                renderInput={(params) => (
                  <TextField {...params} label="Vendor*" />
                )}
              />
              <TextField
                id="outlined-basic"
                size="medium"
                label="Campaign*"
                value={campaignDetails.name}
                variant="outlined"
                sx={{ width: "50%" }}
                disabled
              />
            </Stack>
            <Stack direction={"row"} spacing={2}>
              <TextField
                id="outlined-multiline-static"
                label="Description*"
                multiline
                rows={4}
                sx={{ width: "100%" }}
              />
            </Stack>
            <Stack direction={"row"} spacing={2}>
              <TextField
                id="outlined-basic"
                size="medium"
                label="Type of Expenses (If Applicable)"
                variant="outlined"
                sx={{ width: "100%" }}
              />
            </Stack>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Checkbox size="small" />
              <Typography variant="subtitle1">GST (If Applicable)</Typography>
            </Box>
            <Stack direction={"row"} spacing={2}>
              <TextField
                id="outlined-basic"
                size="medium"
                label="Total Amount*"
                variant="outlined"
                sx={{ width: "30%" }}
              />
              <TextField
                id="outlined-basic"
                size="medium"
                label="GST Amount"
                variant="outlined"
                sx={{ width: "30%" }}
              />
              <TextField
                id="outlined-basic"
                size="medium"
                label="Payable Amount*"
                variant="outlined"
                sx={{ width: "30%" }}
              />
              <FormControl sx={{ width: "10%" }}>
                <InputLabel id="demo-simple-select-label">%</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  label="Percentage"
                >
                  <MenuItem value={1}>1%</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Box sx={{ marginTop: 2, textAlign: "center" }}>
              <Button variant="outlined">Submit</Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </>
  );
}
*/
